import React from 'react';

import { WorkflowRowBase } from '@/components/services/shared';
import type { FirmServiceWorkflowSchema } from '@/services/api/generated/firmsService';

import { WorkflowConfigEditor } from './config';

interface WorkflowRowProps {
  firmId: string;
  serviceId: string;
  workflow: FirmServiceWorkflowSchema;
  disabled: boolean;
  onToggle: (enabled: boolean) => Promise<void> | void;
  onNotesChange: (notes: string) => Promise<void>;
}

/**
 * Workflow row component for firm services configuration.
 *
 * Uses shared WorkflowRowBase for consistent UI/UX with matter services.
 * Firm-specific addition: WorkflowConfigEditor for workflow settings.
 */
export const WorkflowRow: React.FC<WorkflowRowProps> = ({
  firmId,
  serviceId,
  workflow,
  disabled,
  onToggle,
  onNotesChange,
}) => {
  const isDisabledForEditing = disabled || !workflow.is_enabled;
  const hasConfigFields =
    workflow.config_fields && workflow.config_fields.length > 0;

  return (
    <WorkflowRowBase
      workflow={{
        workflow_code: workflow.workflow_code,
        display_name: workflow.display_name,
        description: workflow.description ?? null,
        status: workflow.status,
        is_enabled: workflow.is_enabled,
        notes: workflow.notes ?? null,
        task_types: workflow.task_types,
      }}
      disabled={disabled}
      serviceDisabled={false}
      onToggle={onToggle}
      onNotesChange={onNotesChange}
      guidelineContext="firm"
    >
      {/* Firm-specific: Workflow configuration editor */}
      {hasConfigFields && (
        <div
          className={`mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800 ${
            !workflow.is_enabled ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          <WorkflowConfigEditor
            firmId={firmId}
            serviceId={serviceId}
            workflow={workflow}
            disabled={isDisabledForEditing}
          />
        </div>
      )}
    </WorkflowRowBase>
  );
};
